import {
  Application,
  StarterKitEnvironment,
  UnlockStateFileInput,
} from '@costco-service-catalog/bff-types';
import { FormikHelpers, useFormik } from 'formik';
import { object, string } from 'yup';
import {
  useEnqueueSnackbar,
  useListApplications,
  useUnlockStateFile,
} from '../../../../hooks';
import { utilitiesPayloadType } from '../../../../pages/ApplicationSummaryPage/useApplicationSummaryPage';

type defaultFormValuesType = {
  selectedApplication?: Application;
  selectedEnvironment?: StarterKitEnvironment;
  storageAccountName?: string;
  tfStateFilePath?: string;
};

const useUnlockStateFileModal = (
  handleModalClose: () => void,
  popup: utilitiesPayloadType | undefined
) => {
  const { pushSnackbar } = useEnqueueSnackbar();
  const { data: listApplicationsData, loading: listApplicationsLoading } =
    useListApplications();

  const { unlockStateFile } = useUnlockStateFile();
  const initialApp = popup ? popup.data : undefined;

  const unlockStateFileValidationSchema = () =>
    object({
      selectedApplication: object().required('You must select an application.'),
      selectedEnvironment: object().required(
        'You must select an environment type.'
      ),
      storageAccountName: string().required(
        'You must enter the storage account name.'
      ),
      tfStateFilePath: string()
        .required('You must enter the terraform state file path.')
        .test({
          name: 'validPath',
          exclusive: false,
          params: {},
          message:
            'Path must contain selected application name and environment name.',
          test: (value, context) =>
            value.includes(
              context.parent.selectedApplication.applicationName
            ) && value.includes(context.parent.selectedEnvironment.name),
        }),
    });

  const formik = useFormik({
    initialValues: { selectedApplication: initialApp },
    validationSchema: unlockStateFileValidationSchema(),
    onSubmit: async (
      values: defaultFormValuesType,
      helpers: FormikHelpers<defaultFormValuesType>
    ) => {
      const unlockStateFileInput: UnlockStateFileInput = {
        applicationId: values.selectedApplication?.id || '',
        environmentName: values.selectedEnvironment?.name || '',
        storageAccountName: values.storageAccountName || '',
        tfStateFilePath: values.tfStateFilePath || '',
      };

      const submitOptions = {
        variables: { input: unlockStateFileInput },
        onCompleted: () => {
          // This is intentional
        },
      };
      const submitUnlockStateFile = await unlockStateFile(submitOptions);
      if (
        !submitUnlockStateFile.errors ||
        submitUnlockStateFile.errors?.length === 0
      ) {
        pushSnackbar('Terraform state file has been unlocked successfully.', {
          variant: 'success',
        });
        handleModalClose();
      }
    },
  });

  const selectApplicationFieldError = formik.touched.selectedApplication
    ? Boolean(formik.errors.selectedApplication)
    : false;
  const selectedEnvironmentFieldError = formik.touched.selectedEnvironment
    ? Boolean(formik.errors.selectedEnvironment)
    : false;
  const storageAccountNameFieldError = formik.touched.storageAccountName
    ? Boolean(formik.errors.storageAccountName)
    : false;
  const tfStateFilePathFieldError = formik.touched.tfStateFilePath
    ? Boolean(formik.errors.tfStateFilePath)
    : false;

  return {
    formik,
    selectApplicationFieldError,
    selectedEnvironmentFieldError,
    storageAccountNameFieldError,
    tfStateFilePathFieldError,
    listApplicationsData,
    listApplicationsLoading,
  };
};

export default useUnlockStateFileModal;
